"use client"

import { CheckCircle2, Link2, Key, QrCode, Copy, Check, Terminal } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { QRCode } from "@/components/ui/qr-code"
import { useState } from "react"
import { copyToClipboard } from '@/utils/clipboard'

interface UploadCompleteCardProps {
  progress?: number
  embedded?: boolean
  shareLink?: string
  shareCode?: string
  fileCount?: number
  fileSize?: string
  speed?: string
  uploadPhase?: 'idle' | 'hashing' | 'uploading' | 'completing' | 'done' | 'error'
  onTransferAgain?: () => void
  onCopy?: () => void
}

export function UploadCompleteCard({ 
  progress = 100, 
  embedded = false, 
  shareLink = "", 
  shareCode = "",
  fileCount = 1,
  fileSize = "",
  speed = "",
  uploadPhase,
  onTransferAgain,
  onCopy
}: UploadCompleteCardProps) {
  // 判断是否完成
  const isComplete = uploadPhase === 'done' || progress >= 100
  
  // 复制状态
  const [copiedLink, setCopiedLink] = useState(false)
  const [copiedCode, setCopiedCode] = useState(false)
  const [showQrDialog, setShowQrDialog] = useState(false)
  
  // 复制链接
  const handleCopyLink = async () => {
    if (!shareLink) return
    const success = await copyToClipboard(shareLink)
    if (success) {
      setCopiedLink(true)
      setTimeout(() => setCopiedLink(false), 2000)
    }
  }
  
  // 复制口令
  const handleCopyCode = async () => {
    if (!shareCode) return
    const success = await copyToClipboard(shareCode)
    if (success) {
      setCopiedCode(true)
      setTimeout(() => setCopiedCode(false), 2000)
    }
  }

  // 命令行下载复制状态
  const [copiedWget, setCopiedWget] = useState(false)
  const [copiedCurl, setCopiedCurl] = useState(false)

  // 获取终端直链
  const getDirectDownloadUrl = (link: string, code?: string) => {
    if (!link) return ''
    let apiUrl = link
    if (apiUrl.includes('/download/group/')) {
      apiUrl = apiUrl.replace('/download/group/', '/api/download/group/')
    } else if (!apiUrl.includes('/api/')) {
      apiUrl = apiUrl.replace(/\/download\//, '/api/download/')
    }
    if (!apiUrl.startsWith('http://') && !apiUrl.startsWith('https://')) {
      const origin = typeof window !== 'undefined' ? window.location.origin : ''
      apiUrl = `${origin}${apiUrl.startsWith('/') ? '' : '/'}${apiUrl}`
    }
    if (code && code.trim()) {
      const separator = apiUrl.includes('?') ? '&' : '?'
      apiUrl += `${separator}password=${encodeURIComponent(code.trim())}`
    }
    return apiUrl
  }

  // 复制 wget 命令
  const handleCopyWget = async () => {
    if (!shareLink) return
    const downloadUrl = getDirectDownloadUrl(shareLink, shareCode)
    const cmd = `wget --content-disposition "${downloadUrl}"`
    const success = await copyToClipboard(cmd)
    if (success) {
      setCopiedWget(true)
      setTimeout(() => setCopiedWget(false), 2000)
    }
  }

  // 复制 curl 命令
  const handleCopyCurl = async () => {
    if (!shareLink) return
    const downloadUrl = getDirectDownloadUrl(shareLink, shareCode)
    const cmd = `curl -OJ "${downloadUrl}"`
    const success = await copyToClipboard(cmd)
    if (success) {
      setCopiedCurl(true)
      setTimeout(() => setCopiedCurl(false), 2000)
    }
  }
  
  const content = (
    <>
      {/* 圆形进度球或完成图标 - 固定高度区域 */}
      <div className="flex flex-col items-center h-[140px] justify-center">
        {isComplete ? (
          // 完成状态 - 显示对勾图标
          <div className="w-28 h-28 bg-yellow-400 rounded-full flex items-center justify-center shadow-sm">
            <CheckCircle2 className="w-14 h-14 text-white" />
          </div>
        ) : (
          // 上传中状态 - 显示进度圆环
          <div className="relative w-28 h-28">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 112 112">
              {/* 背景圆环 */}
              <circle cx="56" cy="56" r="50" stroke="#f3f4f6" strokeWidth="6" fill="none" />
              {/* 进度圆环 */}
              <circle
                cx="56"
                cy="56"
                r="50"
                stroke="#fbbf24"
                strokeWidth="6"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 50}`}
                strokeDashoffset={`${2 * Math.PI * 50 * (1 - progress / 100)}`}
                strokeLinecap="round"
                className="transition-all duration-500 ease-out"
              />
            </svg>
            {/* 中心文字 */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold text-gray-900 leading-none">
                {Math.round(progress)}
                <span className="text-lg">%</span>
              </span>
              <span className="text-[10px] text-gray-400 mt-0.5">
                {uploadPhase === 'hashing' ? '准备中' : (speed || '计算中...')}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* 标题区域 - 固定高度 */}
      <div className="text-center h-[44px] flex flex-col justify-center">
        <h3 className="text-base font-bold text-gray-900">
          {isComplete
            ? '已完成'
            : uploadPhase === 'hashing'
            ? '正在准备文件...'
            : uploadPhase === 'completing'
            ? '正在合并处理...'
            : '传输中'}
        </h3>
        <p className="text-xs text-gray-500 mt-0.5">
          {isComplete
            ? '传输完成'
            : uploadPhase === 'hashing'
            ? '计算文件校验码中'
            : uploadPhase === 'completing'
            ? '分块传输完成，服务器确认中'
            : `${fileCount} 个文件 ${fileSize ? `| ${fileSize}` : ''}`}
        </p>
      </div>

      {/* 分享区域 */}
      <div className="space-y-2.5 pt-3">
        <p className="text-xs font-medium text-gray-700 px-1">链接或口令分享</p>

        {/* 链接框 - 带复制按钮 */}
        <div className="relative group">
          <div className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg border border-transparent hover:border-gray-200 transition-colors min-w-0">
            <Link2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            {shareLink ? (
              <span className="text-xs text-gray-600 flex-1 min-w-0 truncate">{shareLink}</span>
            ) : (
              <span className="text-xs text-gray-400 italic flex-1 min-w-0">传输完成后生成链接</span>
            )}
          </div>
          {shareLink && (
            <button
              onClick={handleCopyLink}
              className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-[10px] bg-white hover:bg-gray-100 text-gray-700 rounded border border-gray-200 transition-all opacity-100 sm:opacity-0 sm:group-hover:opacity-100 flex items-center gap-1 shadow-sm"
            >
              {copiedLink ? (
                <>
                  <Check className="w-3 h-3 text-green-600" />
                  <span className="text-green-600">已复制</span>
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  <span>复制链接</span>
                </>
              )}
            </button>
          )}
        </div>

        {/* 口令框 - 带复制按钮 */}
        <div className="relative group">
          <div className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg border border-transparent hover:border-gray-200 transition-colors">
            <Key className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            {shareCode ? (
              <span className="text-xs text-gray-600">{shareCode}</span>
            ) : (
              <span className="text-xs text-gray-400 italic">传输完成后生成口令</span>
            )}
          </div>
          {shareCode && (
            <button
              onClick={handleCopyCode}
              className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-[10px] bg-white hover:bg-gray-100 text-gray-700 rounded border border-gray-200 transition-all opacity-100 sm:opacity-0 sm:group-hover:opacity-100 flex items-center gap-1"
            >
              {copiedCode ? (
                <>
                  <Check className="w-3 h-3 text-green-600" />
                  <span className="text-green-600">已复制</span>
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  <span>复制</span>
                </>
              )}
            </button>
          )}
        </div>

        {/* 其他方式区域 - 固定高度占位 */}
        <div className="h-[36px] flex items-center">
          {isComplete && shareLink && (
            <div className="flex items-center gap-1.5 px-1 w-full text-xs">
              <span className="text-xs text-gray-500 flex-shrink-0">其他方式:</span>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7 hover:bg-yellow-100 hover:text-gray-900 transition-colors flex-shrink-0"
                title="扫码取件"
                onClick={() => setShowQrDialog(true)}
              >
                <QrCode className="w-3.5 h-3.5 text-gray-700" />
              </Button>

              <button
                type="button"
                onClick={handleCopyWget}
                title="复制 wget 下载命令"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-mono bg-gray-100 hover:bg-gray-200 text-gray-700 transition-all active:scale-95 border border-transparent hover:border-gray-300"
              >
                {copiedWget ? (
                  <>
                    <Check className="w-3 h-3 text-green-600" />
                    <span className="text-green-600 font-sans font-medium">已复制</span>
                  </>
                ) : (
                  <>
                    <Terminal className="w-3 h-3 text-gray-500" />
                    <span>wget</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleCopyCurl}
                title="复制 curl 下载命令"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-mono bg-gray-100 hover:bg-gray-200 text-gray-700 transition-all active:scale-95 border border-transparent hover:border-gray-300"
              >
                {copiedCurl ? (
                  <>
                    <Check className="w-3 h-3 text-green-600" />
                    <span className="text-green-600 font-sans font-medium">已复制</span>
                  </>
                ) : (
                  <>
                    <Terminal className="w-3 h-3 text-gray-500" />
                    <span>curl</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* 按钮区域 - 固定高度 */}
        <div className="flex gap-2 pt-2 h-[44px]">
          {isComplete ? (
            <>
              <Button 
                className="flex-1 h-11 bg-yellow-400 hover:bg-yellow-500 text-gray-900 rounded-full font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={onCopy}
                disabled={!shareLink && !shareCode}
              >
                一键复制
              </Button>
              {onTransferAgain && (
                <Button
                  variant="outline"
                  className="flex-1 h-11 border-gray-300 text-gray-700 rounded-full font-medium bg-white hover:bg-gray-50 text-sm"
                  onClick={onTransferAgain}
                >
                  再传一次
                </Button>
              )}
            </>
          ) : (
            <Button 
              className="w-full h-11 bg-yellow-400/60 text-gray-500 rounded-full font-medium text-sm cursor-not-allowed"
              disabled
            >
              上传中...
            </Button>
          )}
        </div>
      </div>

      {/* 二维码取件弹窗 */}
      <Dialog open={showQrDialog} onOpenChange={setShowQrDialog}>
        <DialogContent className="sm:max-w-xs text-center p-6 bg-white rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-gray-900">扫码下载文件</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              手机相机、微信、系统浏览器扫一扫即可下载
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center justify-center my-3 space-y-2">
            <QRCode value={shareLink} size={160} />
            {shareCode && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-100 text-xs">
                <span className="text-gray-500">取件口令:</span>
                <span className="font-mono font-bold text-gray-900">{shareCode}</span>
              </div>
            )}
          </div>

          <div className="flex justify-center">
            <Button
              className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-medium rounded-full h-9 text-xs"
              onClick={() => {
                copyToClipboard(shareLink)
                setShowQrDialog(false)
              }}
            >
              复制下载链接并关闭
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )

  if (embedded) {
    return <div className="px-4 w-full max-w-[320px] mx-auto">{content}</div>
  }

  return (
    <Card className="bg-white rounded-2xl shadow-lg px-5 py-5 w-full max-w-[280px]">{content}</Card>
  )
}
